import React, { useEffect, useState } from "react";
import { User, Crown, Settings, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import ProfileModal from "./ProfileModal";
import SubscriptionModal from "./SubscriptionModal";
import PackagesModal from "./PackagesModal";
import LanguageSwitcher from "./LanguageSwitcher";
import { useSubscription } from "../hooks/useSubscription";
import Logo from '../assests/favicon-logo.png'
import SuccessModal from "./sub-components/successModal";
import CancellationModal from "./sub-components/cancelModal";

interface HeaderProps {
  onLogout: () => void;
}

const Header: React.FC<HeaderProps> = ({ onLogout }) => {
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showPackagesModal, setShowPackagesModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const { t } = useTranslation();

  const { subscription, loading } = useSubscription();

  const remainingAnalyses = subscription?.analyses_remaining ?? 0;

  // Check for payment success/cancellation and auto-open subscription modal
  useEffect(() => {
    // if (props.isOpen) {
      console.log('SubscriptionModal opened, checking for payment status...');
      const urlParams = new URLSearchParams(window.location.search);
      const paymentSuccess = urlParams.get('payment_success');
      const paymentCancelled = urlParams.get('payment_cancelled');
      const planId = urlParams.get('plan_id');
      if (paymentSuccess === 'true' && planId) {
        setShowSuccessModal(true);
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      } else if (paymentCancelled === 'true') {
        setShowCancellationModal(true);
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
        sessionStorage.removeItem('attempted_plan_purchase');
      }
    // }
  }, []);
  const planName = subscription?.plan ?? "free";
  const analysesRemaining =
    !loading && subscription ? subscription.analyses_remaining : null;
  const handleUpgrade = () => {
    // Reload data after upgrade
    window.location.reload();
  };

  const handleLogout = () => {
    if (confirm(t("app.logout_confirm"))) {
      onLogout();
    }
  };

  // Prevent background scrolling when any modal is open
  useEffect(() => {
    const isAnyModalOpen = showProfileModal || showSubscriptionModal || showPackagesModal || showSuccessModal || showCancellationModal;
    
    if (isAnyModalOpen) {
      // Store current scroll position
      const scrollY = window.scrollY;
      
      // Disable scrolling and preserve position
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      // Re-enable scrolling and restore position
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    // Cleanup function to ensure styles are reset
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [showProfileModal, showSubscriptionModal, showPackagesModal, showSuccessModal, showCancellationModal]);

  return (
    <>
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-6 px-4 sm:py-10 sm:px-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="flex-1 w-full">
            <div className="flex gap-x-2">
              <img src={Logo} className='max-h-10 max-w-10 min-h-10 min-w-10 rounded-full'/>
              <h1 className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2">
                {t("header.title")}
              </h1>
            </div>
            <p className="text-base sm:text-lg opacity-90 mb-1 sm:mb-3">
              {t("header.subtitle")}
            </p>
            <p className="text-xs sm:text-sm opacity-75 font-medium">
              {t("header.author").includes("Travail en Suisse") ? (
                <>
                  {t("header.author").split("Travail en Suisse")[0]}
                  <a
                    href="https://www.instagram.com/travail.en.suisse/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:opacity-100 transition-opacity"
                  >
                    Travail en Suisse
                  </a>
                  {t("header.author").split("Travail en Suisse")[1]}
                </>
              ) : t("header.author").includes("Work in Switzerland") ? (
                <>
                  {t("header.author").split("Work in Switzerland")[0]}
                  <a
                    href="https://www.instagram.com/travail.en.suisse/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:opacity-100 transition-opacity"
                  >
                    Work in Switzerland
                  </a>
                  {t("header.author").split("Work in Switzerland")[1]}
                </>
              ) : (
                t("header.author")
              )}
            </p>
          </div>

          <div className="flex flex-row flex-wrap items-center space-x-2 sm:space-x-3 space-y-2 sm:space-y-0 mt-4 sm:mt-0">
            {/* Language Switcher */}
            <LanguageSwitcher />
            {/* Plan indicator */}
            <button
              onClick={() => setShowPackagesModal(true)}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm min-w-[120px] transition-all transform hover:scale-105"
              title={t("header.upgrade_plan")}
            >
              <div className="flex items-center">
                {planName === "pro" ? (
                  <Crown className="w-4 h-4 mr-1 text-yellow-300" />
                ) : planName === "avance" ? (
                  <Crown className="w-4 h-4 mr-1 text-green-300" />
                ) : planName === "decouverte" ? (
                  <Crown className="w-4 h-4 mr-1 text-blue-300" />
                ) : (
                  <User className="w-4 h-4 mr-1" />
                )}
                <span className="font-medium">
                  {t(`header.packages`)}
                </span>
              </div>
              <div className="text-[10px] sm:text-xs opacity-75">
                {loading ? (
                  <span className="animate-spin inline-block">⏳</span>
                ) : (
                  analysesRemaining
                )}{" "}
                {t("header.analyses_remaining")}
              </div>
            </button>

            {/* Profile button */}
            <button
              onClick={() => setShowProfileModal(true)}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg p-2 sm:p-3 transition-all transform hover:scale-105"
              title={t("header.profile")}
            >
              <User className="w-5 h-5" />
            </button>

            {/* Subscription button */}
            <button
              onClick={() => setShowSubscriptionModal(true)}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg p-2 sm:p-3 transition-all transform hover:scale-105"
              title={t("header.subscription")}
            >
              <Settings className="w-5 h-5" />
            </button>

            {/* Logout button */}
            <button
              onClick={handleLogout}
              className="bg-red-500 bg-opacity-80 hover:bg-opacity-100 rounded-lg p-2 sm:p-3 transition-all transform hover:scale-105"
              title={t("header.logout")}
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {showProfileModal && (
        <ProfileModal
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          handlePackagesModal={() => setShowPackagesModal(true)}
        />
      )}

      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
      />

      <PackagesModal
        isOpen={showPackagesModal}
        onClose={() => setShowPackagesModal(false)}
        onUpgrade={handleUpgrade}
        currentPlan={planName}
        remainingAnalyses={remainingAnalyses}
      />

      {showSuccessModal && (
        <SuccessModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
        />
      )}
      {showCancellationModal && (
        <CancellationModal
          isOpen={showCancellationModal}
          onClose={() => setShowCancellationModal(false)}
        />
      )}
    </>
  );
};

export default Header;
